// マイページ再注文機能
export namespace Reorder {
  
  // 再注文アイテムの型定義
  interface ReorderItem {
    variant_id: string;
    quantity: number;
    product_title: string;
    is_addon: boolean;
    properties: Record<string, string>;
  }

  // 現在の言語を取得（統一ロジック: 日本のみ日本語、他は英語）
  function getCurrentLanguage(): string {
    const locale = (window as any).wakoFlowLocale;
    const pathname = window.location.pathname;
    const urlBasedLanguage = pathname.startsWith('/en/') ? 'en' : 'ja';
    
    if (locale === 'ja') {
      return 'ja';
    } else if (locale) {
      // 日本語以外のlocaleが設定されている場合は英語
      return 'en';
    } else {
      // localeが未設定の場合はURLから判定
      return urlBasedLanguage;
    }
  }

  // 直接カート追加（全ての再注文で使用）
  async function reorderDirect(button: HTMLButtonElement): Promise<void> {
    button.disabled = true;
    button.textContent = '処理中...';

    try {
      const orderItemsData = button.dataset.reorderItems;
      
      if (!orderItemsData) {
        alert('注文データが見つかりません。');
        return;
      }

      // HTMLエスケープを解除
      const unescapedData = unescapeHtml(orderItemsData);
      const reorderItems: ReorderItem[] = JSON.parse(unescapedData);

      // 現在の言語を取得
      const currentLanguage = getCurrentLanguage();
      const cartAddUrl = '/cart/add.js'; // 統一URL（全マーケット共通）

      // カート追加データを準備
      const cartData = {
        items: reorderItems.map(item => ({
          id: item.variant_id,
          quantity: item.quantity,
          properties: item.properties
        }))
      };

      // Shopify Cart APIを使用してカートに追加
      const response = await fetch(cartAddUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(cartData),
      });

      if (!response.ok) {
        const errorData = await response.text();
        console.error('カート追加エラー:', errorData);
        
        // 詳細なエラー処理
        try {
          const errorJson = JSON.parse(errorData);
          const errorMessage = parseShopifyCartError(errorJson, currentLanguage);
          alert(errorMessage);
          return;
        } catch {
          // JSON パースに失敗した場合は汎用エラー
          throw new Error(`カート追加に失敗しました: ${response.status}`);
        }
      }

      const result = await response.json();

      alert(currentLanguage === 'en' ? 'Added to cart successfully.' : '商品をカートに追加しました。');
      
      // カートページにリダイレクト
      const cartUrl = '/cart'; // 統一URL（全マーケット共通）
      window.location.href = cartUrl;

    } catch (error) {
      console.error('再注文エラー:', error);
      const errorLanguage = getCurrentLanguage();
      if (error instanceof SyntaxError) {
        alert(errorLanguage === 'en' ? 'Order data format error.' : '注文データの形式に問題があります。');
      } else {
        alert(errorLanguage === 'en' ? 'Reorder failed. Please try again.' : '再注文に失敗しました。もう一度お試しください。');
      }
    } finally {
      button.disabled = false;
      button.textContent = '再注文する';
    }
  }

  // HTMLエスケープを解除する関数
  function unescapeHtml(html: string): string {
    const textarea = document.createElement('textarea');
    textarea.innerHTML = html;
    return textarea.value;
  }

  // Shopify Cart APIエラーを解析してユーザーフレンドリーなメッセージに変換
  function parseShopifyCartError(errorJson: any, language: string): string {
    const isJapanese = language !== 'en';
    
    // 商品・バリアント関連エラー
    if (errorJson.errors) {
      const errors = errorJson.errors;
      
      // バリアントが利用できない
      if (Object.values(errors).some((error: any) => 
        typeof error === 'string' && error.includes('not available'))) {
        return isJapanese ? 
          '選択した商品またはオプションが現在利用できません。商品が削除されているか、在庫切れの可能性があります。' :
          'The selected product or variant is not available. It may have been deleted or is out of stock.';
      }
      
      // 在庫不足
      if (Object.values(errors).some((error: any) => 
        typeof error === 'string' && error.includes('inventory'))) {
        return isJapanese ? 
          '在庫が不足しています。数量を調整してください。' :
          'Insufficient inventory. Please adjust the quantity.';
      }
      
      // 数量制限エラー
      if (Object.values(errors).some((error: any) => 
        typeof error === 'string' && error.includes('quantity'))) {
        return isJapanese ? 
          '注文数量に問題があります。' :
          'There is an issue with the order quantity.';
      }
    }
    
    // 汎用エラーメッセージ
    return isJapanese ? 
      '商品をカートに追加できませんでした。商品の設定が変更されている可能性があります。' :
      'Could not add products to cart. Product settings may have been changed.';
  }

  // 初期化関数
  export function init(): void {
    // 全ての再注文ボタンで直接カート追加（過去ファイルURL使用）
    document.addEventListener('click', (event) => {
      const target = event.target as HTMLElement;
      
      if (target.classList.contains('reorder-direct')) {
        event.preventDefault();
        reorderDirect(target as HTMLButtonElement);
      }
    });
  }
} 